import Head from 'next/head'
import Image from 'next/image'
import swr from '../lib/swr';
import { useLayoutEffect, useState, useEffect } from 'react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dynamic from 'next/dynamic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faStar, faCodeBranch, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';

const Slider = dynamic(() => import('react-slick'), { ssr: false });


export default function Projects({ enabled, setEnabled, seo, staticProjectsData }) {
  const { data: _projects } = swr('/api/projects');
  const { data: githubRepos, error: githubError } = swr('/api/repos');
  const projects = _projects ? _projects : null;
  const [isMobile, setIsMobile] = useState(false);
  const [allProjects, setAllProjects] = useState([]);

  // Détection de la taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Vérification initiale
    handleResize();
    
    // Écoute des changements de taille
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

    // Données des projets pour l'affichage
  const displayProjects = allProjects;

  // Fusion des projets statiques et GitHub
  useEffect(() => {
    const staticProjects = [
      /*{
        id: 1,
        title: "Portfolio",
        subtitle: "Site web personnel",
        description: "Next JS - Tailwind CSS - Vercel",
        link: "https://www.sd-orlog.me",
        linkText: "orlogsd.tech / sd-orlog.me",
        type: "static",
        images: [
          "/assets/portfo/1.png",
          "/assets/portfo/2.png",
          "/assets/portfo/3.png",
          "/assets/portfo/4.png",
          "/assets/portfo/5.png"
        ]
      },*/
      {
        id: 2,
        title: "Coteazy Web App",
        subtitle: "Plateforme de cagnottes et tontines - client",
        description: "React JS - Redux",
        type: "static",
        images: [
          "/assets/coteazy/1.jpg",
          "/assets/coteazy/2.jpg",
          "/assets/coteazy/3.jpg",
          "/assets/coteazy/4.jpg",
          "/assets/coteazy/5.jpg"
        ]
      },
      {
        id: 3,
        title: "Coteazy Dashboard",
        subtitle: "Plateforme de cagnottes et tontines - admin",
        description: "Laravel - Vue JS - Tailwind CSS",
        type: "static",
        images: [
          "/assets/cotadmin/4.PNG",
          "/assets/cotadmin/1.png",
          "/assets/cotadmin/2.png",
          "/assets/cotadmin/3.png"
        ]
      },
      {
        id: 4,
        title: "G.E.S PVO",
        subtitle: "Gestion et suivi des véhicules au PVO",
        description: "Laravel - Bootstrap - MySQL - Redis",
        type: "static",
        images: [
          "/assets/pvo/1.jpg",
          "/assets/pvo/2.jpg",
          "/assets/pvo/3.jpg",
          "/assets/pvo/4.jpg",
          "/assets/pvo/5.jpg"
        ]
      },
      {
        id: 5,
        title: "EDULAB",
        subtitle: "Plateforme d'e-learning et de mentorat pour etudiants developpeurs",
        description: "React JS - Material UI - Spring Boot",
        type: "static",
        images: [
          "/assets/edulab/1.jpg",
          "/assets/edulab/2.jpg",
          "/assets/edulab/3.jpg",
          "/assets/edulab/4.jpg",
          "/assets/edulab/5.jpg"
        ]
      },
      {
        id: 6,
        title: "BMICALC",
        subtitle: "Calculateur d'IMC avec historique et statistiques",
        description: "Laravel - Bootstrap - JQuery - MySQL",
        type: "static",
        images: [
          "/assets/bmi/1.png",
          "/assets/bmi/2.png",
          "/assets/bmi/3.png",
          "/assets/bmi/4.png"
        ]
      }
    ];

    let combinedProjects = [...staticProjects];

    // Ajouter les projets GitHub s'ils sont disponibles
    if (githubRepos && Array.isArray(githubRepos)) {
      const githubProjects = githubRepos.map((repo, index) => ({
        id: `github-${repo.id}`,
        title: repo.title,
        subtitle: `Projet ${repo.language} ${repo.stars > 0 ? `- ⭐ ${repo.stars}` : ''}`,
        description: `${repo.description} ${repo.topics && repo.topics.length > 0 ? `\nTags: ${repo.topics.join(', ')}` : ''}`,
        link: repo.html_url,
        linkText: "Voir sur GitHub",
        homepage: repo.homepage,
        type: "github",
        language: repo.language,
        stars: repo.stars,
        forks: repo.forks,
        images: [repo.defaultImage] // Une seule image par défaut pour les projets GitHub
      }));

      combinedProjects = [...combinedProjects, ...githubProjects];
    }

    setAllProjects(combinedProjects);
  }, [githubRepos]);

  useLayoutEffect(() => {
    // Gestion du thème
    if (enabled) {
      let body = document.getElementById('page');
      body.classList.remove('whitetheme')
      body.classList.add('blacktheme')
    } else {
      let body = document.getElementById('page');
      body.classList.remove('blacktheme')
      body.classList.add('whitetheme')
    }
  }, [enabled]);

  // Configuration responsive pour le slider
  var settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          arrows: false,
          dots: true
        }
      },
      {
        breakpoint: 600,
        settings: {
          arrows: false,
          dots: true,
          dotsClass: "slick-dots slick-thumb"
        }
      }
    ]
  };

  // Données des projets
  const projectsData = allProjects;

  return (
    <div className="w-full overflow-x-hidden">
      <Head>
        <title>Projets & Réalisations - Didi Orlog SOSSOU - Développeur FullStack & Data Scientist</title>
        <meta name="description" content="Découvrez mes projets de développement web, applications IA et solutions data science. Portfolio incluant React, Node.js, Python, Laravel et projets machine learning." />
        <meta name="keywords" content="projets développement web, portfolio développeur, applications react, nodejs, python, IA, machine learning, data science, laravel, togo" />
        
        {/* Open Graph pour la page projets */}
        <meta property="og:title" content="Projets & Réalisations - Didi Orlog SOSSOU" />
        <meta property="og:description" content="Portfolio de projets de développement web, applications IA et solutions data science" />
        <meta property="og:url" content="https://www.orlogsd.dev/projects" />
        
        {/* Twitter Cards pour la page projets */}
        <meta name="twitter:title" content="Projets & Réalisations - Didi Orlog SOSSOU" />
        <meta name="twitter:description" content="Portfolio de projets de développement web et IA" />
        
        {/* Données structurées pour les projets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Projets de Didi Orlog SOSSOU",
            "description": "Portfolio de projets de développement web, applications IA et solutions data science réalisés par Didi Orlog SOSSOU",
            "url": "https://www.orlogsd.dev/projects",
            "author": {
              "@type": "Person",
              "name": "Didi Orlog SOSSOU",
              "jobTitle": "Développeur FullStack & Data Scientist",
              "url": "https://www.orlogsd.dev"
            },
            "hasPart": [
              {
                "@type": "CreativeWork",
                "name": "Draavis - Plateforme de location de véhicules",
                "description": "SaaS de location de véhicules développé avec React/Node.js",
                "creator": {
                  "@type": "Person", 
                  "name": "Didi Orlog SOSSOU"
                },
                "programmingLanguage": ["JavaScript", "TypeScript", "React", "Node.js"],
                "url": "https://www.draavis.com"
              },
              {
                "@type": "CreativeWork", 
                "name": "Système de gestion PVO",
                "description": "Application de gestion des entrées/sorties véhicules au Port Autonome de Lomé",
                "creator": {
                  "@type": "Person",
                  "name": "Didi Orlog SOSSOU"
                },
                "programmingLanguage": ["PHP", "Laravel", "MySQL"]
              },
              {
                "@type": "CreativeWork",
                "name": "Plateforme de tontines en ligne",
                "description": "Application web pour la gestion de cagnottes et tontines collaboratives",
                "creator": {
                  "@type": "Person",
                  "name": "Didi Orlog SOSSOU"
                },
                "programmingLanguage": ["PHP", "Laravel", "JavaScript"]
              }
            ]
          })}
        </script>
      </Head>
      
      <div className="py-10 md:py-20 justify-center align-center mx-auto mobile-safe">
        <p className={"text-2xl md:text-3xl font-semibold text-center mb-2 " + (enabled ? ' text-white' : ' text-black')}>
          Quelques réalisations 
        </p>
        <p className={"text-lg md:text-xl font-normal text-center mb-5 pb-4 px-4" + (enabled ? ' text-white/70' : ' text-black/60')}>
          Voici quelques projets sur lesquels j'ai travaillé, que ce soit dans un cadre professionnel, éducatif ou personnel.
        </p>

        {/* Indicateur de chargement pour les projets GitHub */}
        {githubError && (
          <div className="text-center mb-4">
            <p className={`text-sm ${enabled ? 'text-orange-400' : 'text-orange-600'}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              Impossible de charger les projets GitHub
            </p>
          </div>
        )}

        {!githubRepos && !githubError && (
          <div className="text-center mb-4">
            <div className="inline-flex items-center">
              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-2"></div>
              <p className={`text-sm ${enabled ? 'text-white' : 'text-gray-600'}`}>
                Chargement des projets GitHub...
              </p>
            </div>
          </div>
        )}

        <div className="projects-grid grid w-full m-auto justify-center justify-items-center px-4">
          {displayProjects.map((project) => (
            <div
              key={project.id}
              className={`project-card ${
                enabled ? 'bg-gray-800' : 'bg-gray-50'
              } rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                project.type === 'github' ? 'border-l-4 border-orange-500' : ''
              } flex flex-col h-full`}
            >
              <div className="slider-container">
                {isMobile || project.type === 'github' ? (
                  // Affichage d'une seule image sur mobile (< 768px) ou pour les projets GitHub
                  <div className="slider-image-wrapper relative">
                    <img 
                      src={project.images[0]} 
                      alt={`${project.title} - Image principale`}
                      className="w-full h-48 md:h-56 object-cover"
                      loading="lazy"
                    />
                    {/* Badge pour les projets GitHub */}
                    {project.type === 'github' && (
                      <div className="absolute top-2 right-2 bg-black/80 text-white px-2 py-1 rounded-full text-xs flex items-center">
                        <FontAwesomeIcon icon={faGithub} className="mr-1" />
                        {project.language}
                      </div>
                    )}
                  </div>
                ) : (
                  // Affichage du carousel sur desktop (>= 768px) pour les projets statiques
                  <Slider {...settings}>
                    {project.images.map((image, idx) => (
                      <div key={idx} className="slider-image-wrapper">
                        <img 
                          src={image} 
                          alt={`${project.title} - Image ${idx + 1}`}
                          className="w-full h-48 md:h-56 object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </Slider>
                )}
              </div>
              
              <div className="card-content p-4 md:p-6 flex-1 flex flex-col">
                <div className="card-text flex-grow space-y-3">
                  <h3 className={`font-semibold text-lg md:text-xl ${enabled ? 'text-white' : 'text-gray-700'}`}>
                    {project.title}
                    {project.link && (
                      <>
                        {' - '}
                        <a 
                          href={project.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className='text-orange-500 hover:text-orange-600 transition-colors duration-200'
                        >
                          {project.linkText}
                        </a>
                      </>
                    )}
                  </h3>
                  
                  <p className={`text-sm md:text-base font-semibold leading-relaxed ${
                    enabled ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    {project.subtitle}
                  </p>
                  
                  <p className={`text-sm md:text-base leading-relaxed whitespace-pre-line ${
                    enabled ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {project.description}
                  </p>

                  {/* Statistiques pour les projets GitHub */}
                  {project.type === 'github' && (
                    <div className="flex items-center space-x-4 text-sm">
                      {project.stars > 0 && (
                        <span className={`flex items-center ${enabled ? 'text-yellow-400' : 'text-yellow-600'}`}>
                          <FontAwesomeIcon icon={faStar} className="mr-1" />
                          {project.stars}
                        </span>
                      )}
                      {project.forks > 0 && (
                        <span className={`flex items-center ${enabled ? 'text-blue-400' : 'text-blue-600'}`}>
                          <FontAwesomeIcon icon={faCodeBranch} className="mr-1" />
                          {project.forks}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="card-actions mt-auto pt-4">
                  <div className="flex space-x-2">
                     {project.link &&<button
                      className="bg-orange-500 hover:bg-orange-600 flex-1 flex justify-center items-center py-3 text-white font-semibold transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95"
                      onClick={() => project.link && window.open(project.link, '_blank')}
                    >
                      <FontAwesomeIcon icon={project.type === 'github' ? faGithub : faEye} className="mr-2 transition-transform duration-200 hover:rotate-12" />
                      {project.type === 'github' ? 'GitHub' : 'Voir le projet'}
                    </button>}
                    
                    {/* Bouton pour le site web si disponible (projets GitHub) */}
                    {project.homepage && (
                      <button
                        className="bg-blue-500 hover:bg-blue-600 flex-1 flex justify-center items-center py-3 text-white font-semibold transition-all duration-300 rounded-lg transform hover:scale-105 active:scale-95"
                        onClick={() => window.open(project.homepage, '_blank')}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                        Site web
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export async function getStaticProps() {
  // Pré-générer les données côté serveur pour le SEO
  const pageData = {
    enabled: true,
    seo: {
      title: "Projets & Réalisations - Didi Orlog SOSSOU | Développeur FullStack & Data Scientist",
      description: "Portfolio complet de mes réalisations : applications web React/Laravel, solutions IA/ML, projets data science. Découvrez mes projets de développement fullstack et intelligence artificielle.",
      keywords: "projets développement web, portfolio développeur fullstack, applications react, projets laravel, projets python, solutions IA, machine learning, data science, nodejs, développeur togo",
    },
    projectsData: {
      // Pré-charger les données statiques des projets principaux pour le SEO
      staticProjects: [
        {
          name: "Coteazy Web App",
          description: "Plateforme de cagnottes et tontines - React JS - Redux",
          tech: ["React", "Redux", "JavaScript"]
        },
        {
          name: "Coteazy Dashboard", 
          description: "Plateforme de cagnottes et tontines - admin - Laravel - Vue JS - Tailwind CSS",
          tech: ["Laravel", "Vue.js", "Tailwind CSS"]
        },
        {
          name: "G.E.S PVO",
          description: "Gestion et suivi des véhicules au PVO - Laravel - Bootstrap - MySQL - Redis", 
          tech: ["Laravel", "Bootstrap", "MySQL", "Redis"]
        },
        {
          name: "EDULAB",
          description: "Plateforme éducative - Laravel - Bootstrap",
          tech: ["Laravel", "Bootstrap", "PHP"]
        }
      ]
    },
    lastUpdated: new Date().toISOString(),
  }

  return {
    props: pageData,
    revalidate: 3600, // Régénérer toutes les heures pour les projets
  }
}
